import { AppDispatch, RootState } from "src/store/store";
import { AppRoutes, StatusCode } from "src/common/constants/constantEnums";
import {
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Heading,
  Input,
  Text,
  VStack,
} from "@chakra-ui/react";
import { EnglishLabels, Regisration_Form_Labels_English } from "src/common/constants/englishLabels";
import { GlobalUtils, toCamelCase } from "src/utils/globalUtils";
import { HindiLabels, Regisration_Form_Labels_Hindi } from "src/common/constants/hindiLabels";
import { HomeVertical, MksLogo } from "../resources/icons";
import { Link, useNavigate, useLocation } from "react-router-dom";
import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { AuthService } from "src/rest/authService";
import { GlobalLangSelector } from "src/common/globalLangSelector";
import { PasswordInput } from "src/common/passwordInput";
import { UseAppToast } from "src/common/useAppToast";

export const LoginPage: React.FC = () => {
  const globalInfo = useSelector((state: RootState) => state.globalInfo);
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const { errorToast, successToast } = UseAppToast();
  const { globalLanguage } = globalInfo;
  const isHindiLabels = globalLanguage === "HI";
  const isLoggedIn = Boolean(localStorage.getItem("authToken"));
  const [emailId, setEmailId] = useState("");
  const [password, setPassword] = useState("");
  const [isEmailInValid, setIsEmailInValid] = useState(false);

  useEffect(() => {
    GlobalUtils.setGlobalLanguage(dispatch);
  }, [dispatch]);

  useEffect(() => {
    if (isLoggedIn) {
      navigate("/", { replace: true });
    }
  }, [isLoggedIn, navigate]);

  const login = async (): Promise<void> => {
    try {
      const response = await AuthService.login(emailId, password);
      if (response.status === StatusCode.SUCCESS_STATUS_CODE) {
        const { token, email, mobileNo, metaData, applicationInfo } = response.data || {};
        const role = GlobalUtils.getAuthStateFromToken(token).role;
        const application_status = GlobalUtils.getAuthStateFromToken(token).applicationStatus;
        const approved = GlobalUtils.getAuthStateFromToken(token).approved;

        // Reject hard-block: if backend marks user as rejected by status/flag
        if (application_status === "Rejected") {
          GlobalUtils.clearSession();
          errorToast("Login denied. Your application has been rejected.");
          return;
        }

        // If unapproved, handle first
        if (approved === false && (role === "ADMIN" || role === "SUPER_ADMIN")) {
          errorToast(
            "Login denied. Your admin account is blocked. Please contact support."
          );
          return;
        }

        // Unapproved non-admin users: allow access to Submit page only
        if (
          approved === false &&
          role === "USER" &&
          application_status === "Completed"
        ) {
          const md = metaData || {};
          const bd = md?.BusinessDetails || {};
          const cached = {
            fullName: md?.Name || (email || "").split("@")[0] || "",
            dob: md?.DOB || "",
            contactNo: md?.Contact || mobileNo || "",
            educationalQualification: bd?.educationalQualification || "",
            occupation: bd?.occupation || "",
            businessDetails: bd?.businessDetails || "",
          };
          GlobalUtils.setToken(token);
          window.localStorage.setItem("cachedSubmitDetails", JSON.stringify(cached));
          GlobalUtils.setUserLoginInfo(email, mobileNo);
          window.localStorage.setItem("pendingAccess", "true");
          successToast("Logged in. Limited access until approval.");
          navigate(AppRoutes.SUBMIT, { replace: true, state: { fromApp: true } });
          return;
        }

        // DRAFT USER RESUME LOGIC
        if (applicationInfo && application_status === "In_Progress") {
          GlobalUtils.setToken(token); // Ensure auth token is set for draft users
          window.localStorage.setItem("applicationInfo", JSON.stringify(applicationInfo));
          GlobalUtils.setUserLoginInfo(email, mobileNo);
          window.localStorage.setItem("pendingAccess", "false");
          successToast("Draft found. Resuming your registration.");
          navigate(AppRoutes.REGISTRATION_FORM, { replace: true, state: { fromApp: true } });
          return;
        }

        // Approved users must have a valid token
        if (!token) {
          errorToast(
            "Login denied. Unable to start session. Please try again or contact support."
          );
          return;
        }

        GlobalUtils.setToken(token);
        GlobalUtils.setUserLoginInfo(email, mobileNo);
        window.localStorage.removeItem("cachedSubmitDetails");
        window.localStorage.removeItem("pendingAccess");

        // If user came from a protected route, return them there.
        const fromPathname = (location.state as any)?.from?.pathname as string | undefined;
        const fromSearch = (location.state as any)?.from?.search as string | undefined;
        const from = fromPathname ? `${fromPathname}${fromSearch || ""}` : null;

        if (from) {
          navigate(from, { replace: true });
          return;
        }

        if (role === "ADMIN" || role === "SUPER_ADMIN") {
          navigate("/admin", { replace: true, state: { fromApp: true } });
        } else if (role === "CONSULTANT") {
          // Store consultant-specific info; profile page will read this instead of calling the API
          GlobalUtils.setConsultantInfo({
            id: response.data?.id ?? 0,
            email: email ?? "",
            fullName: response.data?.fullName ?? "",
            mobileNo: mobileNo ?? "",
            dailyRequestCountLimit: response.data?.dailyRequestCountLimit ?? 0,
            marriage_profile_limit: response.data?.marriage_profile_limit ?? 0,
          });
          navigate("/profile", { replace: true, state: { fromApp: true } });
        } else {
          navigate("/profile", { replace: true, state: { fromApp: true } });
        }
      }
    } catch (error) {
      errorToast("Invalid email ID or password.");
    }
  };

  const validateEmail = (): void => {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    setIsEmailInValid(!emailRegex.test(emailId));
  };

  return (
    <Grid templateColumns="repeat(6, 1fr)" gap="2rem" height="100vh">
      <GridItem colSpan={{ lg: 4 }} hideBelow="lg">
        <HomeVertical />
      </GridItem>
      <GridItem colSpan={{ sm: 6, lg: 2 }}>
        <VStack m="2rem" spacing="1rem">
          <Flex
            height="5rem"
            width="100%"
            direction="row"
            justifyContent="space-between"
            alignItems="center"
          >
            <Box alignSelf="flex-start" height="4rem" width="4rem">
              <MksLogo />
            </Box>
            <Flex alignContent="flex-end" width="calc(100%-4rem)">
              <GlobalLangSelector />
              <Link to={AppRoutes.REGISTER} state={{ fromApp: true }}>
                <Button ml="0.5rem" variant="mks" alignItems="center">
                  <Heading color="white" fontSize="large">
                    {isHindiLabels
                      ? HindiLabels.Register
                      : EnglishLabels.Register}
                  </Heading>
                </Button>
              </Link>
            </Flex>
          </Flex>
          <VStack
            maxWidth={{ xs: "100%", sm: "65%", lg: "100%" }}
            spacing="1.5rem"
          >
            <Flex width="100%" direction="column" justifyContent="flex-start">
              <Heading fontSize="70" wordBreak="break-word">
                {isHindiLabels ? HindiLabels.Login_Page : EnglishLabels.Login}
              </Heading>
              <Text wordBreak="break-word">
                {isHindiLabels
                  ? HindiLabels.Welcome_Label
                  : EnglishLabels.Welcome_Label}
              </Text>
            </Flex>
            <VStack alignItems="center" width="100%" spacing="1rem">
              <VStack width="100%" alignItems="start">
                <Input
                  isInvalid={isEmailInValid}
                  textColor={isEmailInValid ? "#E53E3E" : "black"}
                  onBlur={validateEmail}
                  background="rgba(108, 117, 125, 0.08)"
                  borderColor="transparent"
                  className="form-input"
                  onChange={(e) => {
                    setEmailId(e.target.value);
                    isEmailInValid && setIsEmailInValid(false);
                  }}
                  placeholder={
                    isHindiLabels
                      ? HindiLabels.Enter_Email
                      : EnglishLabels.Enter_Email
                  }
                />
                {isEmailInValid && (
                  <Text color="#E53E3E">
                    {isHindiLabels
                      ? `${Regisration_Form_Labels_Hindi.EMAIL_ID} अमान्य`
                      : `Invalid ${toCamelCase(Regisration_Form_Labels_English.EMAIL_ID)}`}
                  </Text>
                )}
              </VStack>
              <PasswordInput
                setPassword={setPassword}
                placeholder={
                  isHindiLabels ? HindiLabels.Enter_Password : "Enter Password"
                }
              />
            </VStack>
            <Button
              isDisabled={isEmailInValid || password.trim() === ""}
              width="100%"
              variant="mks"
              alignItems="center"
              onClick={() => login()}
            >
              <Heading color="white" fontSize="large">
                {isHindiLabels ? HindiLabels.Login : EnglishLabels.Login}
              </Heading>
            </Button>
            <Button
              width="100%"
              border="1px solid #4A2C2A"
              borderRadius="0.5rem"
              background="white"
              alignItems="center"
              as={Link}
              to={"/"}
            >
              <Heading
                color="#4A2C2A"
                fontSize="large"
                fontFamily="PP Neue Montreal"
              >
                {isHindiLabels
                  ? HindiLabels.Back_to_Home
                  : EnglishLabels.Back_to_Home}
              </Heading>
            </Button>
            <HStack width="100%" justifyContent="space-between">
              <Text
                textAlign="start"
                fontSize={14}
                display="inline"
                _hover={{
                  textColor: "#FF7E6B",
                  textDecoration: "underline",
                  cursor: "pointer",
                }}
                onClick={() => navigate(AppRoutes.FORGOT_PASSWORD)}
              >
                {isHindiLabels
                  ? HindiLabels.Forgot_Password
                  : EnglishLabels.Forgot_Password}
              </Text>
              <Text textAlign="end" fontSize={14}>
                {isHindiLabels
                  ? HindiLabels.Dont_Have_Account
                  : EnglishLabels.Dont_Have_Account}
                <Link to={AppRoutes.REGISTER}>
                  <Text
                    fontSize={14}
                    ml="0.5rem"
                    display="inline"
                    _hover={{
                      textColor: "#FF7E6B",
                      textDecoration: "underline",
                    }}
                  >
                    {isHindiLabels
                      ? HindiLabels.Register
                      : EnglishLabels.Register}
                  </Text>
                </Link>
              </Text>
            </HStack>
          </VStack>
        </VStack>
      </GridItem>
    </Grid>
  );
};

export default LoginPage;
